import { useState, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getRoot } from 'lexical';
import { datadogLogs } from '@datadog/browser-logs';
import 'amazon-connect-streams';
import { DraftEmailContact, EmailClient } from '@amazon-connect/email';
import { useEmailContext } from '../../../../../../contexts/EmailContext';
import { sendEmail } from '../../../../../../services/sendEmail';
import { fetchEmailContentOnceBodyApproved } from '../../../../../../services/fetchEmailContent';
import { EmailAddress, EmailAttachment, EmailMessage, SendEmailRequest } from '../../../../../../models/Email';
import { resolveEmailAttachments } from '../../../../../../services/resolveEmailAttachments';
import { Attachment } from '../../../../../../models/Attachment';
import { $generateHtmlFromNodes } from '@lexical/html';
import { OutboundEmailFields } from '../../Outbound/EmailOutboundHeader';
import { normalizeSubject, replySubject } from '../../../../../../utils/emailContactHelpers';
import config from '../../../../../../config';
import { areReplyRecipientsSendable, getReplyRecipients } from '../../../../../../utils/replyRecipients';

interface UseSendEmailProps {
  onDiscard: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  outboundFields?: OutboundEmailFields;
  attachments?: Attachment[];
}

interface UseSendEmailReturn {
  handleSend: () => Promise<void>;
  isLoading: boolean;
}

export function useSendEmail({
  onDiscard,
  onSuccess,
  onError,
  outboundFields,
  attachments = []
}: UseSendEmailProps): UseSendEmailReturn {
  const [editor] = useLexicalComposerContext();
  const {
    selectedEmail,
    emailData,
    emailThreads = {},
    emailDrafts = {},
    appendMessageToThread,
    templateHtml
  } = useEmailContext();
  const [isLoading, setIsLoading] = useState(false);
  const agentUserIdRef = useRef<string>('');
  const emailClientRef = useRef<EmailClient | null>(null);
  const selectedEmailRef = useRef(selectedEmail);

  useEffect(() => {
    selectedEmailRef.current = selectedEmail;
  }, [selectedEmail]);

  useEffect(() => {
    connect.agent(agent => {
      const arn = agent.getAgentARN();
      const agentId = arn.split('/').pop() ?? arn;
      agentUserIdRef.current = agentId;
      emailClientRef.current = new EmailClient(connect.core.getSDKClientConfig() as any);
    });
  }, []);

  const handleSend = async (): Promise<void> => {
    if (!selectedEmail) return;

    // selectedEmail in the context may change during the sendMail
    const capturedContactId = selectedEmail;
    const isOutbound = !!outboundFields;

    let bodyHtml = '';

    editor.read(() => {
      const editorHtml = $generateHtmlFromNodes(editor);
      bodyHtml = templateHtml ? `${editorHtml}${templateHtml}` : editorHtml;
    });

    setIsLoading(true);

    try {
      validateAttachmentsUploaded();

      let sent: boolean;
      if (isOutbound) {
        sent = await handleSendOutbound(capturedContactId, bodyHtml);
      } else {
        sent = await handleSendReply(capturedContactId, bodyHtml);
      }

      if (sent) {
        // The agent may have switched to a different thread while the send was in flight -
        // the editor instance is shared across threads, so only clear it if it's still showing
        // the thread that was just sent.
        if (selectedEmailRef.current === capturedContactId) {
          editor.update(() => {
            const root = $getRoot();
            root.clear();
            root.append($createParagraphNode());
          });
        }
        onSuccess();
        onDiscard();
      }
    } catch (error) {
      datadogLogs.logger.error('Failed to send email', { channel: 'email' }, error as Error);

      const errorMessage = (error instanceof Error && error.message) || 'Failed to send email. Please try again.';

      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const validateAttachmentsUploaded = (): void => {
    const missingUpload = attachments.find(attachment => !attachment.fileId);

    if (missingUpload) {
      throw new Error(`Attachment "${missingUpload.name}" is not uploaded yet`);
    }
  };

  // Every attachment is uploaded before a send is allowed, so these stand in when the fetch back fails.
  const composedAttachments = (): EmailAttachment[] =>
    attachments.flatMap(({ fileId, name }) => (fileId ? [{ fileId, fileName: name }] : []));

  const handleSendOutbound = async (contactId: string, bodyHtml: string): Promise<boolean> => {
    const client = emailClientRef.current;

    if (!client || !outboundFields) {
      datadogLogs.logger.error('Outbound email send skipped: client or fields not available', { channel: 'email' });
      return false;
    }

    const toAddress = outboundFields.to.trim();
    if (!toAddress) {
      datadogLogs.logger.error('Outbound email send skipped: recipient address is empty', { channel: 'email' });
      return false;
    }

    const cc = outboundFields.cc
      ?.split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(emailAddress => ({ emailAddress }));

    const sendEmailPayload: DraftEmailContact = {
      contactId,
      to: [{ emailAddress: toAddress }],
      cc: cc?.length ? cc : undefined,
      emailContent: {
        subject: outboundFields.subject,
        body: bodyHtml,
        bodyType: 'text/html' as const
      }
    };

    try {
      await client.sendEmail(sendEmailPayload);
    } catch (err) {
      datadogLogs.logger.error('Failed to send outbound email', { channel: 'email' }, err as Error);
      throw err;
    }

    let sentEmailData: Awaited<ReturnType<EmailClient['getEmailData']>> | undefined;
    let sentBodyHtml: string | undefined;
    let sentOtherRecipients: EmailAddress[] | undefined;
    let sentAttachments: EmailAttachment[] | undefined;

    //TODO: Sanitize sentBodyHtml with DOMPurify - it reaches dangerouslySetInnerHTML unsanitized
    try {
      sentEmailData = await client.getEmailData({ contactId, activeContactId: contactId });
      const sentContent = await fetchEmailContentOnceBodyApproved(contactId, sentEmailData.bodyLocation?.fileId);
      sentBodyHtml = sentContent?.bodyHtml ?? undefined;
      sentOtherRecipients = sentContent?.otherRecipients?.map(emailAddress => ({ emailAddress }));
      sentAttachments = await resolveEmailAttachments(sentEmailData.attachmentLocations, contactId);
    } catch (fetchError) {
      datadogLogs.logger.error('Failed to fetch sent outbound email body', { channel: 'email' }, fetchError as Error);
    }

    const sentMessage: EmailMessage = {
      contactId,
      subject: sentEmailData?.subject ?? outboundFields.subject,
      from: sentEmailData?.from,
      to: sentEmailData?.to ?? [{ emailAddress: toAddress }],
      cc: sentEmailData?.cc ?? cc ?? [],
      otherRecipients: sentOtherRecipients,
      bodyHtml: sentBodyHtml ?? bodyHtml,
      attachments: sentAttachments ?? composedAttachments(),
      timestamp: Date.now(),
      contentLoaded: true
    };

    appendMessageToThread(contactId, sentMessage);
    return true;
  };

  const handleSendReply = async (contactId: string, bodyHtml: string): Promise<boolean> => {
    const data = emailData[contactId];
    const recipients = getReplyRecipients(data, emailThreads[contactId], emailDrafts[contactId]);

    if (!data || !data.to?.[0] || !areReplyRecipientsSendable(recipients)) {
      datadogLogs.logger.error('Reply email send skipped: contact data is incomplete', { channel: 'email' });
      return false;
    }

    const toAddress = recipients.to[0];
    const toRecipient =
      toAddress === data.from ? { address: toAddress, displayName: data.customerName } : { address: toAddress };

    const client = emailClientRef.current;
    const draftContactId = data.replyDraftContactId;

    if (!client) {
      datadogLogs.logger.error('Reply email send skipped: email client not available', { channel: 'email' });
      return false;
    }

    if (!draftContactId) {
      datadogLogs.logger.error('Reply email send skipped: draft contact ID not yet available', { channel: 'email' });
      throw new Error('Failed to send email. Please discard and start a new reply.');
    }

    const subject = replySubject(data.subject);

    const sendEmailPayload: SendEmailRequest = {
      agentUserId: agentUserIdRef.current,
      from: { address: data.to[0] },
      to: toRecipient,
      otherRecipients: recipients.otherRecipients.map(address => ({ address })),
      subject,
      bodyHtml,
      inReplyToContactId: contactId,
      idempotencyKey: crypto.randomUUID(),
      contactFlowId: config.emailContactFlowId || undefined,
      draftContactId
    };

    try {
      await sendEmail(sendEmailPayload);
    } catch (err) {
      datadogLogs.logger.error('Failed to send reply email', { channel: 'email' }, err as Error);
      throw err;
    }

    let sentEmailData: Awaited<ReturnType<EmailClient['getEmailData']>> | undefined;
    let sentBodyHtml: string | undefined;
    let sentOtherRecipients: EmailAddress[] | undefined;
    let sentAttachments: EmailAttachment[] | undefined;

    //TODO: Sanitize sentBodyHtml with DOMPurify - it reaches dangerouslySetInnerHTML unsanitized
    try {
      sentEmailData = await client.getEmailData({ contactId: draftContactId, activeContactId: contactId });
      const sentContent = await fetchEmailContentOnceBodyApproved(draftContactId, sentEmailData.bodyLocation?.fileId);
      sentBodyHtml = sentContent?.bodyHtml ?? undefined;
      sentOtherRecipients = sentContent?.otherRecipients?.map(emailAddress => ({ emailAddress }));
      sentAttachments = await resolveEmailAttachments(sentEmailData.attachmentLocations, draftContactId);
    } catch (fetchError) {
      datadogLogs.logger.error('Failed to fetch sent reply email body', { channel: 'email' }, fetchError as Error);
    }

    const sentMessage: EmailMessage = {
      contactId: draftContactId,
      subject: normalizeSubject(sentEmailData?.subject) ?? subject,
      from: sentEmailData?.from ?? { emailAddress: data.to[0] },
      to: sentEmailData?.deliveredTo
        ? [sentEmailData.deliveredTo]
        : (sentEmailData?.to ?? [{ emailAddress: toRecipient.address, displayName: toRecipient.displayName }]),
      cc: sentEmailData?.cc ?? [],
      otherRecipients: sentOtherRecipients ?? recipients.otherRecipients.map(emailAddress => ({ emailAddress })),
      bodyHtml: sentBodyHtml ?? bodyHtml,
      attachments: sentAttachments ?? composedAttachments(),
      timestamp: Date.now(),
      contentLoaded: true
    };

    appendMessageToThread(contactId, sentMessage);
    return true;
  };

  return { handleSend, isLoading };
}
